import React, { useState, useEffect } from "react";
import { Form, Button, Card, Typography, Divider, Collapse, Descriptions } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import IntlMessages from "@iso/components/utility/intlMessages";
import FormModalWrapper from "./FormModal.styles";
import { useSelector } from "react-redux";
import _ from "lodash";
import BioForm from "@iso/containers/Pages/AdminPages/FormBuilder/BioForm/BioForm";
import DragDropQuestion from "@iso/components/DragDropQuestion/DragDropQuestion";
import CheckinForm from '@iso/containers/Pages/AdminPages/FormBuilder/CheckinForm/CheckinForm';
import SurveyFormResult from "./SurveyFormResult";
import moment from 'moment';
import FormBuilder from "antd-form-builder";
import { normalizeFieldWidget, isSignatureField, withRowDividers } from "@iso/constants/libraryFieldWidgets";
// Side-effect: registers the shared schema widgets (fixed_text/fixed_image/scale/
// date-view/signature+upload placeholders) exactly once. We override signature/
// upload/host-picker below with read-only view widgets.
import "@iso/lib/helpers/schemaFormWidgets";

const { Title, Text } = Typography;

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const joinUrl = (base, path) =>
  `${(base || '').replace(/\/+$/, '')}/${String(path || '').replace(/^\/+/, '')}`;

const isAbsoluteUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s);

// Resolve an attachment/signature ref { url?, path?, dataUrl? } to a usable URL.
// The two upload paths disagree on which field holds the absolute URL: the token
// upload stores { url: <absolute>, path: <key> }, while the authed attachment
// upload stores { path: <absolute S3 URL>, url: <relative key> }. So prefer
// whichever field is ALREADY an absolute URL (e.g. the S3 object URL), and only
// join a relative key onto API_BASE (filesystem storage) when neither is absolute.
// Also repairs the historical bug where a value was built as API_BASE + path with
// no separator (e.g. "http://localhost:8003uploads/...").
const resolveAssetUrl = (value) => {
  if (!value) {
    return '';
  }
  // A signature is stored as a raw base64 data URL string (like the legacy form);
  // render it directly. Also accept a bare stored URL string.
  if (typeof value === 'string') {
    return value;
  }
  if (value.dataUrl) {
    return value.dataUrl;
  }
  // Prefer whichever field is already an absolute URL (S3/Azure object URL).
  const abs = isAbsoluteUrl(value.url)
    ? value.url
    : (isAbsoluteUrl(value.path) ? value.path : '');
  if (abs) {
    // Repair the historical API_BASE+path-without-separator bug for our OWN domain
    // (e.g. "http://localhost:8003uploads/..."); external URLs (S3) pass through.
    if (API_BASE && abs.startsWith(API_BASE)) {
      let rest = abs.slice(API_BASE.length);
      if (rest && rest.charAt(0) !== '/') {
        rest = `/${rest}`;
      }
      return `${API_BASE.replace(/\/+$/, '')}${rest}`;
    }
    return abs;
  }
  // Neither is absolute — treat it as a relative key (filesystem storage).
  const rel = value.url || value.path || '';
  return rel ? joinUrl(API_BASE, rel) : '';
};

// Read-only view widget for a submitted signature (a base64 data URL string, like
// the legacy form; older submissions may be a { url } / { dataUrl } ref).
const SignatureView = ({ value }) => {
  const src = resolveAssetUrl(value);
  return src ? (
    <img
      src={src}
      alt="signature"
      style={{ maxWidth: 320, maxHeight: 150, border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff' }}
    />
  ) : (
    <span style={{ color: '#bfbfbf' }}>No signature</span>
  );
};

// Read-only view widget for a submitted upload (stored as { name, url, type }).
const FileView = ({ value }) => {
  const url = resolveAssetUrl(value);
  if (!url) {
    return <span style={{ color: '#bfbfbf' }}>No file</span>;
  }
  const name = value.name || 'Attachment';
  // heic/heif are images but no browser reliably renders them inline, so they
  // are treated as downloads — a broken <img> is worse than a working link.
  const isImage =
    /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name) ||
    /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(url) ||
    (/^image\//i.test(value.type || '') && !/heic|heif/i.test(value.type || ''));

  if (isImage) {
    return <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 200 }} />;
  }

  // Anything else — a PDF, a SWMS in Word, a plant register in Excel — is a
  // link that both opens and downloads. `download` names the saved file after
  // what the sender called it rather than the storage key.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={name}
      // The whole view is wrapped in pointer-events:none to make it read-only,
      // which would swallow this click too. A descendant can opt back in, and
      // this is the one thing on the page that has to remain clickable — the
      // attachment is unreachable otherwise.
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, pointerEvents: 'auto' }}
    >
      <PaperClipOutlined />
      <span>{name}</span>
      <span style={{ color: '#8c8c8c' }}>(open or download)</span>
    </a>
  );
};

// Map a schema field to a READ-ONLY view field: host-picker -> select of hosts (so
// the stored id shows its label), signature/upload -> image/link view widgets, and
// everything else normalized as usual. `disabled` makes inputs non-editable.
const viewField = (field) => {
  let f = field;
  if (f.widget === 'host-picker') {
    const { hostOptions, ...rest } = f;
    const options = Array.isArray(hostOptions)
      ? hostOptions.map((o) => ({ value: o.userId, label: o.label }))
      : [];
    f = { ...rest, widget: 'select', options };
  }
  f = normalizeFieldWidget(f);
  if (f.widget === 'signature' || isSignatureField(field)) {
    f = { ...f, widget: SignatureView };
  }
  if (f.widget === 'upload_file') {
    f = { ...f, widget: FileView };
  }
  return f;
};

// antd DatePicker / RangePicker require moment values (they call value.clone()).
// Submitted dates come back as ISO strings, so convert them before they become
// initialValues — otherwise the picker throws "date.clone is not a function".
const momentizeDates = (schemaJson, submission) => {
  const out = submission ? JSON.parse(JSON.stringify(submission)) : {};
  const setAtPath = (path, value) => {
    let cur = out;
    for (let i = 0; i < path.length - 1; i += 1) {
      if (cur[path[i]] == null || typeof cur[path[i]] !== 'object') {
        cur[path[i]] = {};
      }
      cur = cur[path[i]];
    }
    cur[path[path.length - 1]] = value;
  };
  const steps = schemaJson && Array.isArray(schemaJson.steps) ? schemaJson.steps : [];
  steps.forEach((step) => {
    ((step.formMeta && step.formMeta.fields) || []).forEach((field) => {
      if (!field.key) {
        return;
      }
      const path = field.key.split('.');
      const raw = path.reduce((acc, k) => (acc == null ? acc : acc[k]), out);
      if (raw == null || raw === '') {
        return;
      }
      if (field.widget === 'date-picker' || field.widget === 'date' || field.widget === 'time-picker') {
        setAtPath(path, moment(raw));
      } else if (field.widget === 'range-picker' && Array.isArray(raw)) {
        setAtPath(path, raw.map((v) => (v ? moment(v) : v)));
      }
    });
  });
  return out;
};

// Read a possibly-dotted key (name.first) from the nested answers object.
const readByKey = (answers, key) => {
  if (!answers) {
    return undefined;
  }
  if (answers[key] !== undefined) {
    return answers[key];
  }
  return key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), answers);
};

// Crash-proof fallback: list every field as "Label: value" text. Used if the
// widget-based form render throws for any field type — the answers are still shown.
const PlainAnswers = ({ schemaJson, submission }) => {
  const steps = schemaJson && Array.isArray(schemaJson.steps) ? schemaJson.steps : [];
  const fmt = (v) => {
    if (v === null || v === undefined || v === '') {
      return '—';
    }
    if (Array.isArray(v)) {
      return v.map((x) => (typeof x === 'object' ? (x.name || x.url || JSON.stringify(x)) : String(x))).join(', ');
    }
    if (typeof v === 'object') {
      return v.name || v.url || v.dataUrl || JSON.stringify(v);
    }
    if (typeof v === 'boolean') {
      return v ? 'Yes' : 'No';
    }
    return String(v);
  };
  return (
    <div>
      {steps.map((step) =>
        ((step.formMeta && step.formMeta.fields) || [])
          .filter((f) => f.key && f.widget !== 'fixed_text' && f.widget !== 'fixed_image')
          .map((f) => (
            <div key={f.key}>
              <Text strong>{f.label || f.key}: </Text>
              <Text>{fmt(readByKey(submission, f.key))}</Text>
              <Divider style={{ margin: '8px 0' }} />
            </div>
          ))
      )}
    </div>
  );
};

// Catches any render error from the widget-based view (e.g. a widget that rejects a
// stored value type) and shows the plain-text answers instead of crashing the modal.
class SubmissionRenderBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('View Result: schema render failed, showing plain answers.', error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// For a submission with NO schema snapshot (submitted before we versioned schemas),
// keep only the fields it actually answered — so it isn't rendered against the
// CURRENT form's fields (which may have gained/lost questions, e.g. a signature added
// later). Snapshot submissions skip this and render their own exact field set.
const filterSchemaToAnswered = (schemaJson, submission) => {
  if (!schemaJson || !Array.isArray(schemaJson.steps)) {
    return schemaJson;
  }
  const steps = schemaJson.steps
    .map((step) => {
      const fields = ((step.formMeta && step.formMeta.fields) || []).filter((f) => {
        if (!f.key || f.widget === 'fixed_text' || f.widget === 'fixed_image') {
          return false;
        }
        const v = readByKey(submission, f.key);
        return v !== undefined && v !== null && v !== '';
      });
      return { ...step, formMeta: { ...(step.formMeta || {}), fields } };
    })
    .filter((step) => ((step.formMeta && step.formMeta.fields) || []).length > 0);
  return { ...schemaJson, steps };
};

// Render ONE submission as the form the user filled — every field, pre-filled with
// the submitted values, read-only, with the steps flattened into a single list (no
// wizard). antd resolves dotted keys (name.first) against the nested answers object.
const SchemaSubmissionView = ({ schemaJson, submission }) => {
  const [viewForm] = Form.useForm();
  const initialValues = momentizeDates(schemaJson, submission);
  const steps = schemaJson && Array.isArray(schemaJson.steps) ? schemaJson.steps : [];
  return (
    <SubmissionRenderBoundary fallback={<PlainAnswers schemaJson={schemaJson} submission={submission} />}>
      {/* pointer-events:none keeps the real widgets + values but makes the view
          read-only (no editing) without greying everything out like `disabled`. */}
      <div style={{ pointerEvents: 'none' }}>
        <Form form={viewForm} layout="vertical" initialValues={initialValues} className="preview-form-area">
          {steps.map((step, index) => {
            const meta = step.formMeta
              ? {
                  ...step.formMeta,
                  formItemLayout: null,
                  gutter: 24,
                  // Section rules are part of how the form READS, so the
                  // answers render with the same breaks the person filling it
                  // in saw.
                  fields: withRowDividers(
                    (step.formMeta.fields || []).map(viewField),
                    step.formMeta.columns
                  ),
                }
              : null;
            return meta ? <FormBuilder key={step.id || index} meta={meta} form={viewForm} /> : null;
          })}
        </Form>
      </div>
    </SubmissionRenderBoundary>
  );
};

const ViewSurveyResultModal = (props) => {
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const currentUser = useSelector((state) => state.user.user);
  const [form] = Form.useForm();
  const permissions = useSelector((state) => state.permission.permissions);
  const [showForm, setShowForm] = useState(false);
  const [fields, setFields] = useState([]);
  const [result, setResult] = useState([]);
  const [globalFormResult, setGlobalFormResult] = useState([]);

  const cancelModal = () => {
    props.cancelViewSurveyResult();
  };

  useEffect(() => {
    if (props.surveyData) {
      if (props.type == "self_create") setResult(props.surveyData.answer_list);
      if (props.type == "system_forms")
        setGlobalFormResult(
          props.surveyData.global_form_submission
        );
    }
  }, [props.surveyData]);

  const generateContent = (metaContent) => {
    if(!metaContent) return [];
    var contentArr = Object.keys(metaContent).map((key) => [
      key,
      metaContent[key],
    ]);
    // console.log(contentArr)
    // var content = '';
    // _.forEach(contentArr, (value, index) => {
    //   content += (value[0].replace("_", " ")[0].toUpperCase() + value[0].replace("_", " ").substring(1)  + ': ' + value[1] + (index != contentArr.length - 1 ? ", " : ''));
    // })

    return contentArr;
  };

  const renderAnswer = (answer) => {
    if (typeof answer === "object" && answer.signatures) {
      return (
        <div>
          {answer.signatures.map((signature) => {
            return (
              <>
                <img
                  src={signature.full_path}
                ></img>
                <p>{signature.name}</p>
              </>
            );
          })}
        </div>
      );
    }
    //render drag&drop
    if (typeof answer === "object" && answer.question) {
      const value = {
        question: answer.question,
        options: answer.options,
        optionInfo: answer.optionInfo,
        questionInfo: answer.questionInfo,
      }
      return (
        <DragDropQuestion value={value} onChange={()=>{}}></DragDropQuestion>
      );
    }
    return <b> {answer.toString()}</b>;
  };

  const renderGlobalFormResult = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        fields={fields}
        className="form-body"
      >
        <div style={{ overflow: "scroll", width: "100%", height: "600px" }}>
          {result
            ? result.map((value, index) => {
                return (
                  <>
                    <h3>Submission #{ index + 1 }:</h3>
                    <br />
                    {value.map((submission, index2) => {
                      return (
                        <>
                          <h3>{submission.question_content}</h3>Answer:{" "}
                          { renderAnswerContent(submission) }
                          <br />
                          <br />
                        </>
                      )
                    })}
                    <hr/>
                  </>
                );
              })
            : ""
          }
          {
            (globalFormResult) ? renderSystemFormResult(globalFormResult) : ''
          }
        </div>
      </Form>
    );
  }

  const renderMultiSelectionAnswer = (rawAnswerContent) => {
    try {
      const items = Array.isArray(rawAnswerContent)
        ? rawAnswerContent
        : JSON.parse(rawAnswerContent || '[]');
      return items.map((item, idx) => (
        <p key={`multi-selection-${idx}`}><b>{item}</b></p>
      ));
    } catch (error) {
      return <b>{rawAnswerContent}</b>;
    }
  };

  const renderFileAttachment = (file, index) => {
    if (!file) return null;

    // Same ref-shape ambiguity as the schema viewer: prefer whichever of url/path
    // is an absolute URL (S3) over a relative key.
    const url = resolveAssetUrl(file);
    if (!url) return null;

    const name = file.name || `Attachment ${index + 1}`;
    const type = file.type || '';
    const isImage = type.startsWith('image/')
      || /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name)
      || /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(url);

    return (
      <div key={`upload-file-${index}`} style={{ marginBottom: 8 }}>
        {isImage ? (
          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 200 }} />
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        )}
      </div>
    );
  };

  const renderAnswerContent = (answer) => {
    switch (answer.survey_question?.question_type) {
      case "signature":
        return (
          <div>
            <img src={answer.answer_content} alt="Signature" />
          </div>
        );

      case 'multi_selection':
        return <>{renderMultiSelectionAnswer(answer.answer_content)}</>;
      case 'upload_file':
        if (Array.isArray(answer.answer_content)) {
          return <>{answer.answer_content.map(renderFileAttachment)}</>;
        }

        if (typeof answer.answer_content === 'string' && answer.answer_content) {
          return renderFileAttachment({
            url: answer.answer_content,
            name: answer.answer_content.split('/').pop(),
            type: '',
          }, 0);
        }

        return (
          <div>
            <b>No file uploaded</b>
          </div>
        );
      
      default:
        return (<b>{answer.answer_content}</b>);
    }
  };

  const renderSystemFormResult = (resultData) => {
    var newResult = [];
    if (Array.isArray(resultData)) 
    {
      resultData.map((value, index) => {
        var value = generateContent(value);

        newResult.push (
            <>
              <h3>Submission #{ index + 1 }:</h3>
              {value.map((submission, index2) => {
                return (
                 <>
                  <h3>
                    {submission[0].replace("_", " ")[0].toUpperCase() + submission[0].replace("_", " ").substring(1).replace(/(---.*)$/g, "")  + ': '}
                  </h3>
                    Answer: {renderAnswer(submission[1])}<br/><br/>
                  </>
                )
              })}
              <hr/>
            </>
          )
        }
      )
    } else {
      var resultData = generateContent(resultData);

      resultData.map((submission, index) => {
        newResult.push(
          <>
            <h3>
              {submission[0].replace("_", " ")[0].toUpperCase() + submission[0].replace("_", " ").substring(1).replace(/(---.*)$/g, "")  + ': '}
            </h3>
              Answer: {renderAnswer(submission[1])}<br/><br/>
          </>
        )
      });
    }

    return newResult;
  };

  // A dynamic (library / schema_json) form stores its answers in
  // global_form_submission — an array of { <fieldKey>: value, ..., submisison_date }
  // — regardless of whether its type is self_create or checkin_form. Detect it so
  // its response renders here instead of the empty legacy answer_list / hardcoded
  // CheckinForm component.
  const schemaJson = props.surveyData?.form?.schema_json;
  const isSchemaForm = !!(schemaJson && Array.isArray(schemaJson.steps) && schemaJson.steps.length);

  const renderSchemaResult = () => {
    const raw = props.surveyData.global_form_submission;
    const submissions = Array.isArray(raw) ? raw : raw ? [raw] : [];

    if (!submissions.length) {
      return (
        <Card bordered={false}>
          <Text>No response recorded.</Text>
        </Card>
      );
    }

    // Choose the schema to render a submission against: its own snapshot (the form as
    // it was when submitted) if present; otherwise the current form filtered to the
    // fields it actually answered (so old submissions don't show newer/removed fields).
    const schemaFor = (answers, snapshot) =>
      snapshot && Array.isArray(snapshot.steps) && snapshot.steps.length
        ? snapshot
        : filterSchemaToAnswered(schemaJson, answers);

    // Single submission — render the filled form directly.
    if (submissions.length === 1) {
      const { _schema, ...answers } = submissions[0];
      return (
        <div style={{ overflowY: 'auto', width: '100%', maxHeight: '600px' }}>
          <SchemaSubmissionView schemaJson={schemaFor(answers, _schema)} submission={answers} />
        </div>
      );
    }

    // Multiple submissions (same user submitted more than once) — one collapsible
    // panel per submitted time; the form loads when a panel is expanded.
    return (
      <div style={{ overflowY: 'auto', width: '100%', maxHeight: '600px' }}>
        <Collapse>
          {submissions.map((submission, index) => {
            const { _schema, ...answers } = submission;
            const panelKey = `${index}-${answers.submisison_date || ''}`;
            return (
              <Collapse.Panel
                key={panelKey}
                header={answers.submisison_date || `Submission #${index + 1}`}
              >
                <SchemaSubmissionView schemaJson={schemaFor(answers, _schema)} submission={answers} />
              </Collapse.Panel>
            );
          })}
        </Collapse>
      </div>
    );
  };

  const renderFormResult = (type) => {
    if (isSchemaForm) {
      return renderSchemaResult();
    }
    switch (type) {
      case "checkin_form":
        return (
          <CheckinForm
            formData={props.surveyData.global_form_submission}
          ></CheckinForm>
        );
      case "bio_form":
        return (
          <BioForm formData={props.surveyData.global_form_submission}></BioForm>
        );
      case "hazard_form":
        return (
          <SurveyFormResult formType="hazard" formData={props.surveyData.global_form_submission} generateContent={generateContent} renderAnswer={renderAnswer}></SurveyFormResult>
        );
      case "training_form":
        return (
          <SurveyFormResult formType="training" formData={props.surveyData.global_form_submission} generateContent={generateContent} renderAnswer={renderAnswer}></SurveyFormResult>
        );  
      case "illness_form":
        return (
          <SurveyFormResult formType="illness" formData={props.surveyData.global_form_submission} generateContent={generateContent} renderAnswer={renderAnswer}></SurveyFormResult>
        );  
      case "near_miss_form":
        return (
          <SurveyFormResult formType="near_miss" formData={props.surveyData.global_form_submission} generateContent={generateContent} renderAnswer={renderAnswer}></SurveyFormResult>
        );  
      default:
        return renderGlobalFormResult();
    }
  };


  // The check-in details a guest entered on the /property-checkin info screen
  // (Name/Business/Phone/Email/Purpose) before the schema form. Only present when
  // the submission is a visitor/contractor check-in (visitor_info-backed), so this
  // renders nothing for a registered-user submission.
  const renderCheckinDetails = () => {
    // Visitor Management passes the visitor_info row directly (flat fields); a
    // registered-user submission carries the same data under `checkin_info` when
    // its users_forms row is linked to a check-in (else null → section hidden).
    const d = (props.surveyData && props.surveyData.checkin_info) || props.surveyData || {};
    const isVisitorCheckin = !!(d.visitor_type || d.checkin_time);
    if (!isVisitorCheckin) {
      return null;
    }
    const fmt = (t) => (t ? moment(t).format('DD-MM-YYYY HH:mm:ss') : null);
    const items = [
      d.name && { label: 'Name', value: d.name },
      d.business_name && { label: 'Business / Company Name', value: d.business_name },
      d.phone && { label: 'Phone', value: d.phone },
      d.email && { label: 'Email', value: d.email },
      d.visit_purpose && { label: 'Purpose', value: d.visit_purpose },
      d.visitor_type && { label: 'Type', value: d.visitor_type === 'contractor' ? 'Contractor' : 'Visitor' },
      // Check-in and check-out on one full-width row; when the guest hasn't
      // checked out yet, make that explicit instead of hiding it.
      d.checkin_time && {
        label: 'Check-in / Check-out',
        span: 2,
        value: `${fmt(d.checkin_time)}  →  ${fmt(d.checkout_time) || 'Not checked out yet'}`,
      },
    ].filter(Boolean);
    if (!items.length) {
      return null;
    }
    return (
      <Card bordered={false}>
        <Title level={5}>Check-in details</Title>
        <Descriptions column={2} size="small" bordered>
          {items.map((it) => (
            <Descriptions.Item key={it.label} label={it.label} span={it.span || 1}>{it.value}</Descriptions.Item>
          ))}
        </Descriptions>
      </Card>
    );
  };

  return (
    <FormModalWrapper
      width={1200}
      className={props.type === 'bio_form' ? 'bio-form-result' : ''}
      forceRender={true}
      maskClosable={false}
      visible={props.visibleViewSurveyResult}
      title={<IntlMessages id="formPage.modal.surveyResult" />}
      onCancel={cancelModal}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>
      ]}
    >
      <Card bordered={false} >
        <Title level={4}><IntlMessages id="Responder" />: {props.surveyData.user}</Title>
        <Title level={4}>Last Submitted: {moment(props.surveyData.agree_date).format('DD-MM-YYYY HH:mm:ss')}</Title>
      </Card>
      {renderCheckinDetails()}
      <Card bordered={false} >
        {renderFormResult(props.type)}
      </Card>
    </FormModalWrapper>
  );
};

export default ViewSurveyResultModal;
